import { useState, useEffect } from "react";
import { Trans } from "@lingui/react/macro";
import { useLingui } from "@lingui/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { ActivityViewContent } from "./ActivityViewContent";
import { ActivityForm } from "./ActivityForm";
import { DeleteActivityDialog } from "./DeleteActivityDialog";
import { useActivity } from "@/hooks/useActivities";
import { QueryErrorState } from "@/components/ui/query-error-state";

type ActivityModalMode = "view" | "create" | "edit";

interface ActivityModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activityId?: string;
  initialMode?: ActivityModalMode;
}

/**
 * Activity Modal Component
 *
 * Orchestrates view/create/edit modes for activities
 * - View mode: Displays activity details with Edit/Delete actions
 * - Create mode: Shows form for new activity
 * - Edit mode: Shows form with pre-populated data (child/type/time immutable)
 */
export function ActivityModal({
  open,
  onOpenChange,
  activityId,
  initialMode = "view",
}: ActivityModalProps) {
  const { i18n } = useLingui();
  const [mode, setMode] = useState<ActivityModalMode>(initialMode);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Fetch activity data only when viewing/editing and activityId is provided
  const { data: activity, isLoading, error, refetch } = useActivity(activityId || "", {
    enabled: !!activityId && open,
  });

  // Reset mode when modal opens/closes or activityId changes
  useEffect(() => {
    if (open) {
      setMode(activityId ? initialMode : "create");
    } else {
      setMode(initialMode);
      setDeleteDialogOpen(false);
    }
  }, [open, activityId, initialMode]);

  // Handle mode transitions
  const handleEditClick = () => setMode("edit");
  const handleDeleteClick = () => setDeleteDialogOpen(true);
  const handleCancelEdit = () => setMode("view");
  const handleFormSuccess = () => {
    onOpenChange(false);
  };
  const handleDeleteSuccess = () => {
    setDeleteDialogOpen(false);
    onOpenChange(false);
  };

  // Determine dialog title based on mode
  const getDialogTitle = () => {
    switch (mode) {
      case "create":
        return i18n._({ id: "activities.modal.createActivity", message: "Create Activity" });
      case "edit":
        return i18n._({ id: "activities.modal.editActivity", message: "Edit Activity" });
      case "view":
      default:
        return i18n._({ id: "activities.modal.activityDetails", message: "Activity Details" });
    }
  };

  return (
    <>
      {/* The details dialog steps aside while the delete confirm is open, so
          two modals never stack; Cancel brings it back. */}
      <Dialog open={open && !deleteDialogOpen} onOpenChange={onOpenChange}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>{getDialogTitle()}</DialogTitle>
            <DialogDescription className="sr-only">
              {mode === "view" ? (
              <Trans id="common.dialog.detailsDescription">Details of the selected record.</Trans>
            ) : (
              <Trans id="common.dialog.formDescription">Fill in the fields, then save.</Trans>
            )}
            </DialogDescription>
          </DialogHeader>

          {/* Loading State */}
          {isLoading && activityId && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden="true" />
            </div>
          )}

          {/* Error State: a deleted record says so, other failures offer a retry. */}
          {error && (
            <QueryErrorState
              error={error}
              onRetry={() => void refetch()}
              title={i18n._({ id: "activities.modal.errorLoading", message: "Failed to load activity" })}
            />
          )}

          {/* View Mode */}
          {mode === "view" && activity && !isLoading && (
            <ActivityViewContent
              activity={activity}
              onEdit={handleEditClick}
              onDelete={handleDeleteClick}
            />
          )}

          {/* Create Mode */}
          {mode === "create" && (
            <ActivityForm
              mode="create"
              onSuccess={handleFormSuccess}
              onCancel={() => onOpenChange(false)}
            />
          )}

          {/* Edit Mode */}
          {mode === "edit" && activity && (
            <ActivityForm
              mode="edit"
              activity={activity}
              onSuccess={handleFormSuccess}
              onCancel={handleCancelEdit}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      {activity && (
        <DeleteActivityDialog
          activity={activity}
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onSuccess={handleDeleteSuccess}
        />
      )}
    </>
  );
}
